import React, { useCallback, useMemo } from 'react';
import classNames from 'classnames';

import { useOrderContext, useOrderDispatch } from '@/components/modals/SubmitOrder/helpers';
import { State as SubmitOrderState } from '@/components/modals/SubmitOrder/types';
import { ShingleSelector } from '@/components/remote/ShingleSelector/ShingleSelector';
import { PriceBookEntryVm, ProductType, SolarProductType } from '@/types';

import { ShingleColorTile } from '../../../OrderDetailsStep/components/EquipmentDetails/components/ShingleSection/components/ShingleColorTile';
import { ContractDetailsWrapper } from '../ContractDetailsWrapper';

import { Props } from './types';

import styles from './ShingleColor.module.scss';

export const ShingleColor = ({ allPriceBookEntries }: Props) => {
  const {
    order: { shingleColor: selectedShingleColor },
    isAccessProgram,
    isDistribution,
    proposalSolarProductType,
  } = useOrderContext();
  const dispatch = useOrderDispatch();
  const isAccessDistribution = isAccessProgram && isDistribution;
  const allAvailableShingles = useMemo(
    () =>
      allPriceBookEntries.filter(
        ({ product, isProductAvailable }) =>
          product.type === ProductType['Asphalt Shingle'] && Boolean(isProductAvailable),
      ),
    [allPriceBookEntries],
  );

  const handleOnChangeColor = useCallback(
    ({ asphaltShingleColor }: PriceBookEntryVm) => {
      dispatch({
        type: 'UPDATE_ORDER',
        payload: {
          shingleColor: asphaltShingleColor,
        },
      });
    },
    [dispatch],
  );

  const order = useOrderContext();

  const handleOrderChange = useCallback(
    (payload: SubmitOrderState) => {
      dispatch({
        type: 'UPDATE',
        payload,
      });
    },
    [dispatch],
  );

  if (proposalSolarProductType === SolarProductType.TLS2_V2A) {
    return <ShingleSelector onOrderChange={handleOrderChange} order={order} />;
  }

  if (!isAccessDistribution) {
    return null;
  }

  return (
    <ContractDetailsWrapper
      classes={{ container: styles.shingle_color, actions: styles.shingle_color_actions }}
      tabletView
      title="Select a shingle color"
    >
      <div className={styles.shingle_color_grid}>
        {allAvailableShingles.map(asphaltShingle => (
          <ShingleColorTile
            classes={{
              container: classNames(styles.item_container, {
                [styles.item_container_active]:
                  asphaltShingle.asphaltShingleColor === selectedShingleColor,
              }),
              imageWrapper: classNames(styles.item_picture, {
                [styles.item_picture_active]:
                  asphaltShingle.asphaltShingleColor === selectedShingleColor,
              }),
              imageLabel: styles.item_picture_label,
              title: classNames(styles.item_title, {
                [styles.item_title_active]:
                  asphaltShingle.asphaltShingleColor === selectedShingleColor,
              }),
              wrapper: styles.item,
            }}
            isActive={asphaltShingle.asphaltShingleColor === selectedShingleColor}
            key={asphaltShingle.productId}
            selectColor={handleOnChangeColor}
            shingleColor={asphaltShingle}
          />
        ))}
      </div>
    </ContractDetailsWrapper>
  );
};
